import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Iter-396 — numbered page bar below every category/search grid.
 * Windowing: always show page 1 and the last page; show current ±1;
 * collapse gaps into "…". Backend contract (proven by the 15-test
 * suite): ?page=N&limit=L with `total` in the response.
 */
function pageWindow(current, totalPages) {
  const pages = new Set([1, totalPages, current - 1, current, current + 1]);
  const list = [...pages]
    .filter((n) => n >= 1 && n <= totalPages)
    .sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const n of list) {
    if (n - prev > 1) out.push("…");
    out.push(n);
    prev = n;
  }
  return out;
}

export default function Pagination({ page = 1, total = 0, limit = 24, onPage }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;
  const current = Math.min(Math.max(1, page), totalPages);
  const items = pageWindow(current, totalPages);
  const btn =
    "min-w-[2.25rem] h-9 px-2 inline-flex items-center justify-center rounded-lg text-sm font-bold transition-colors";
  return (
    <nav
      data-testid="pagination-bar"
      aria-label="Pagination"
      className="mt-8 flex items-center justify-center gap-1 flex-wrap"
    >
      <button
        type="button"
        data-testid="pagination-prev"
        aria-label="Previous page"
        disabled={current <= 1}
        onClick={() => onPage(current - 1)}
        className={`${btn} text-slate-600 hover:bg-white hover:shadow-card disabled:opacity-35 disabled:pointer-events-none`}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      {items.map((it, i) =>
        it === "…" ? (
          <span key={`gap-${i}`} className="px-1.5 text-slate-400 text-sm font-bold select-none">
            …
          </span>
        ) : (
          <button
            key={it}
            type="button"
            data-testid={`pagination-page-${it}`}
            aria-current={it === current ? "page" : undefined}
            onClick={() => it !== current && onPage(it)}
            className={`${btn} ${
              it === current
                ? "bg-[color:var(--brand-blue)] text-white shadow-card"
                : "text-slate-600 hover:bg-white hover:shadow-card"
            }`}
          >
            {it}
          </button>
        )
      )}
      <button
        type="button"
        data-testid="pagination-next"
        aria-label="Next page"
        disabled={current >= totalPages}
        onClick={() => onPage(current + 1)}
        className={`${btn} text-slate-600 hover:bg-white hover:shadow-card disabled:opacity-35 disabled:pointer-events-none`}
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </nav>
  );
}
